import React from "react";
import Layout from "../Layout";
import TWH2 from "../../../assets/hero11.png";
import offer from "../../../assets/offer.png";
import Iterator from "../../utils/Iterator";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";
import {
  Brush,
  ChartNoAxesColumn,
  Code,
  Languages,
  LayoutGrid,
  MessageSquare,
  MonitorSmartphone,
  Palette,
  PanelTop,
  Repeat,
  ScanQrCode,
  Search,
} from "lucide-react";
import { FaHandFist } from "react-icons/fa6";
import { motion } from "framer-motion";
import kyoto from "../../../assets/Partners/kyoto.jpg";
import hail from "../../../assets/Partners/hail.jpg";
import pizzamaker from "../../../assets/Partners/THE.png";
import homeMade from "../../../assets/Partners/homeMade.png";
import shahabander from "../../../assets/Partners/shahbander.jpg";
import { indexStore } from "../../../store/indexStore";
import UAEDirhamIcon from "../../utils/UAEDirhamIcon";

function HomePage() {
  const { t } = useTranslation();

  return (
    <Layout title={`TWh Menu`}>
      <div className="pb-7 pt-4 md:pt-8 flex flex-col gap-8 md:gap-12">
        <HeroSection t={t} />
        <OfferSection t={t} />
        <FeaturesSection t={t} />
        <PartnersSection t={t} />
        <ServicesSection t={t} />
      </div>
    </Layout>
  );
}

export default HomePage;

const HeroSection = ({ t }: any) => {
  return (
    <section className="px-4 md:px-8 lg:px-12 py-6 md:py-12 flex flex-col-reverse gap-8 md:gap-12 md:flex-row justify-between md:items-center min-h-[70vh] md:min-h-[80vh]">
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="md:max-w-maxWidthHalf flex flex-col gap-4 md:gap-6"
      >
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold whitespace-pre-line leading-tight bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
          {t("HomePage.introduction")}
        </h2>
        <p className="text-sm md:text-base lg:text-lg text-muted-foreground whitespace-pre-line leading-relaxed">
          {t("HomePage.introductionParagraph")}
        </p>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="mt-2"
        >
          <Button
            variant={"default"}
            className="bg-MainBlue hover:bg-blue-600 text-white text-base md:text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {t("pages.getStarted")}
          </Button>
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex justify-center items-center md:w-1/3"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-MainBlue/20 to-blue-600/20 rounded-full blur-3xl animate-pulse"></div>
          <img
            className="m-auto relative z-10 w-full max-w-md md:max-w-lg lg:max-w-xl drop-shadow-2xl"
            src={TWH2}
            alt="Hero"
          />
        </div>
      </motion.div>
    </section>
  );
};
const OfferSection = ({ t }: any) => {
  return (
    <section className="py-4 md:py-8 flex flex-col gap-8 md:gap-10">
      {/* قسم العرض الخاص */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden"
      >
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/10 via-blue-600/5 to-MainBlue/10"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-MainBlue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl"></div>

        <div className="relative container mx-auto p-4 md:px-6">
          <div className="bg-gradient-to-br from-white via-white to-blue-50/50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800/50 rounded-2xl md:rounded-3xl shadow-lg border border-MainBlue/20 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* المحتوى النصي */}
              <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-center gap-4 md:gap-5 order-2 md:order-1">
                <div className="space-y-3 md:space-y-4">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-MainBlue/10 to-blue-600/10 text-MainBlue font-semibold rounded-full text-xs md:text-sm mb-3">
                      {t("gloabl.getOfferNow")}
                    </span>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-3">
                      <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                        {t("gloabl.getUpTo")}
                      </span>
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed hidden md:block">
                      {t("gloabl.getUpTo")}
                    </p>
                  </motion.div>

                  {/* الأسعار */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex flex-col gap-4 pt-2"
                  >
                    <div className="flex items-baseline gap-3 md:gap-4 flex-wrap">
                      <div className="flex flex-col">
                        <span className="text-xs md:text-sm text-muted-foreground line-through mb-1">
                          {t("gloabl.AED")} 119
                        </span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-MainBlue">
                            {t("gloabl.AED")}65
                          </span>
                        </div>
                      </div>
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ type: "spring", delay: 0.4 }}
                        className="relative"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-md opacity-50"></div>
                        <span className="relative text-sm md:text-base lg:text-lg text-white px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-full font-bold shadow-lg whitespace-nowrap">
                          45% OFF
                        </span>
                      </motion.div>
                    </div>

                    <motion.a
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="whatsapp://send?phone=+971528401112&text=Hello%21+I+found+TWHMENU.com+and+I+want+to+create+digital+menu"
                      dir="ltr"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full md:w-auto px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-MainBlue to-blue-600 hover:from-blue-600 hover:to-MainBlue text-white font-semibold text-sm md:text-base rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <span>{t("gloabl.getOffer")}</span>
                      <motion.svg
                        className="w-4 h-4 md:w-5 md:h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        initial={{ x: 0 }}
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </motion.svg>
                    </motion.a>
                  </motion.div>
                </div>
              </div>

              {/* الصورة */}
              <div className="relative order-1 md:order-2 flex items-center justify-center p-6 md:p-8 lg:p-10 bg-gradient-to-br from-MainBlue/5 via-blue-600/5 to-MainBlue/5">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="relative w-full max-w-sm md:max-w-md"
                >
                  {/* تأثيرات خلفية */}
                  <div className="absolute inset-0 bg-gradient-to-r from-MainBlue/20 to-blue-600/20 rounded-full blur-2xl animate-pulse"></div>
                  <div className="absolute -top-8 -right-8 w-32 h-32 bg-MainBlue/10 rounded-full blur-2xl"></div>
                  <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-600/10 rounded-full blur-2xl"></div>
                  
                  {/* الصورة */}
                  <motion.img
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="relative z-10 w-full h-auto drop-shadow-xl"
                    src={offer}
                    alt="Special Offer"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* بطاقات الأسعار */}
      <motion.div
        variants={ServiceItem}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="container mx-auto px-4 md:px-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
          {/* بطاقة الخطة الشهرية */}
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            className="relative rounded-xl md:rounded-2xl border-2 border-gray-200 dark:border-gray-700 text-center p-6 md:p-8 flex flex-col shadow-md hover:shadow-xl transition-all duration-300 bg-card backdrop-blur-sm overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-MainBlue/5 rounded-full blur-2xl group-hover:bg-MainBlue/10 transition-colors"></div>
            
            <div className="relative z-10 space-y-3">
              <h3 className="text-xl md:text-2xl font-bold text-foreground">
                {t("Pricing.monthlyPlanTitle")}
              </h3>
              
              <div className="py-3">
                <div className="flex items-baseline justify-center gap-2">
                  <UAEDirhamIcon className="w-5 h-5 md:w-6 md:h-6 text-MainBlue" />
                  <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-MainBlue">95</span>
                </div>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  /{t("Pricing.perMonth")}
                </p>
                <p className="text-xs md:text-sm text-gray-500 line-through mt-1">
                  <span dir="ltr">
                    <UAEDirhamIcon className="w-3 h-3 md:w-4 md:h-4 inline-block" /> 119
                  </span>
                  /{t("Pricing.perMonth")}
                </p>
              </div>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="/pricing"
                className="inline-block w-full mt-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 px-5 rounded-lg font-semibold text-sm md:text-base shadow-md hover:shadow-lg transition-all"
              >
                {t("gloabl.details")}
              </motion.a>
            </div>
          </motion.div>

          {/* بطاقة الخطة السنوية - المميزة */}
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            className="relative rounded-xl md:rounded-2xl text-center p-6 md:p-8 flex flex-col shadow-xl transition-all duration-300 bg-gradient-to-br from-MainBlue/10 via-blue-600/5 to-MainBlue/10 border-2 border-MainBlue overflow-visible group"
          >
            {/* شارة الأفضل - محسّنة */}
            <motion.div
              initial={{ rotate: -12 }}
              whileHover={{ rotate: 0, scale: 1.05 }}
              className="absolute -top-3 -right-2 md:-top-4 md:-right-3 z-30"
            >
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs md:text-sm font-bold px-3 md:px-4 py-1.5 md:py-2 rounded-full shadow-lg whitespace-nowrap">
                {t("Pricing.bestValue")}
              </div>
            </motion.div>

            <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/10 to-blue-600/10 rounded-xl md:rounded-2xl"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-MainBlue/10 rounded-full blur-3xl group-hover:bg-MainBlue/20 transition-colors"></div>
            
            <div className="relative z-10 space-y-3 pt-2">
              <h3 className="text-xl md:text-2xl font-bold text-foreground">
                {t("Pricing.yearlyPlanTitle")}
              </h3>
              
              <div className="py-3">
                <div className="flex items-baseline justify-center gap-2">
                  <UAEDirhamIcon className="w-5 h-5 md:w-6 md:h-6 text-MainBlue" />
                  <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-MainBlue">780</span>
                </div>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  /{t("Pricing.perYear")}
                </p>
                <p className="text-xs md:text-sm text-gray-500 line-through mt-1">
                  <span dir="ltr">
                    <UAEDirhamIcon className="w-3 h-3 md:w-4 md:h-4 inline-block" /> 1140
                  </span>
                  /{t("Pricing.perYear")}
                </p>
              </div>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="/pricing"
                className="inline-block w-full mt-4 bg-gradient-to-r from-MainBlue to-blue-600 text-white py-2.5 px-5 rounded-lg font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all"
              >
                {t("gloabl.details")}
              </motion.a>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // يخلي العناصر تظهر واحد واحد
    },
  },
};

const FeaturesItem = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const FeaturesSection = ({ t }: any) => {
  const featuresList = features(t);

  return (
    <section className="px-4 md:px-8 lg:px-12 py-8 md:py-12 flex flex-col gap-10 md:gap-14 justify-between items-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex flex-col gap-4 md:gap-5 justify-between items-center text-center"
      >
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
          {t("HomePage.KeyFeatures")}
        </h1>
        <p className="text-sm md:text-base lg:text-lg text-muted-foreground text-center max-w-3xl leading-relaxed">
          {t("HomePage.discribeFeatures")}
        </p>
      </motion.div>
      <motion.div
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 w-full max-w-7xl"
      >
        <Iterator
          data={featuresList}
          render={(e, i) => (
            <motion.div
              key={i}
              variants={FeaturesItem}
              whileHover={{ y: -8, scale: 1.02 }}
              className="flex flex-col gap-4 p-6 md:p-8 rounded-2xl bg-card border border-border/50 shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
            >
              <div className="flex">
                <div className="text-3xl md:text-4xl flex text-MainBlue p-4 md:p-5 items-center bg-gradient-to-br from-MainBlue/10 to-blue-600/10 rounded-2xl group-hover:bg-gradient-to-br group-hover:from-MainBlue/20 group-hover:to-blue-600/20 transition-all duration-300">
                  {e.icon}
                </div>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-foreground group-hover:text-MainBlue transition-colors">
                {e.name}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {e.description}
              </p>
            </motion.div>
          )}
        />
      </motion.div>
    </section>
  );
};
const ServiceItem = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const ServicesSection = ({ t }: any) => {
  const services = [
    {
      icon: <Code className="text-5xl md:text-6xl text-MainBlue" />,
      text: t("HomePage.webDevelopment"),
    },
    {
      icon: <PanelTop className="text-5xl md:text-6xl text-MainBlue" />,
      text: t("HomePage.paymentLinks"),
    },
    {
      icon: <Search className="text-5xl md:text-6xl text-MainBlue" />,
      text: t("HomePage.seo"),
    },
    {
      icon: <Palette className="text-5xl md:text-6xl text-MainBlue" />,
      text: t("HomePage.graphicDesign"),
    },
  ];
  return (
    <section className="px-4 md:px-8 lg:px-12 py-10 md:py-16 flex flex-col gap-10 md:gap-14">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex flex-col gap-4 justify-between items-center text-center"
      >
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
          {t("HomePage.AdvancedSerives")}
        </h1>
      </motion.div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto w-full">
        <Iterator
          data={services}
          render={(s, i) => (
            <motion.div
              key={i}
              variants={ServiceItem}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover={{ y: -8, scale: 1.05 }}
              className="bg-gradient-to-br from-muted via-muted/50 to-muted/30 rounded-xl md:rounded-2xl flex flex-col font-bold gap-4 md:gap-6 p-6 md:p-10 justify-between items-center border border-border/50 shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
            >
              <div className="group-hover:scale-110 transition-transform duration-300">
                {s.icon}
              </div>
              <p className="text-lg md:text-xl lg:text-2xl text-center text-foreground group-hover:text-MainBlue transition-colors">
                {s.text}
              </p>
            </motion.div>
          )}
        />
      </div>
    </section>
  );
};

const PartnersSection = ({ t }: any) => {
  const Partners = [
    {
      image: hail,
      url: "https://twhmenu.com/hail/1//",
    },
    {
      image: kyoto,
      url: "https://twhmenu.com/kyoto/16",
    },
    {
      image: pizzamaker,
      url: "https://twhmenu.com/thepizzamaker/32",
    },
    {
      image: homeMade,
      url: "https://twhmenu.com/HOMEMADEPIZZA/33",
    },
    {
      image: shahabander,
      url: "https://twhmenu.com/SHAHABANDARMANDI",
    },
  ];
  const { lang } = indexStore();
  const isRTL = lang === "ar" ? true : false;

  return (
    <section className="px-4 md:px-8 lg:px-12 py-10 md:py-16 bg-gradient-to-br from-muted via-muted/50 to-muted/30 flex flex-col gap-8 md:gap-12 justify-between">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex flex-col gap-4 text-center"
      >
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center whitespace-pre-line bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
          {t("HomePage.ourPartners")}
        </h2>
      </motion.div>

      <div className="overflow-hidden relative group py-4">
        <div
          className={`flex w-max ${
            isRTL ? "animate-marqueertl" : "animate-marquee"
          } group-hover:[animation-play-state:paused]`}
        >
          {[...Partners, ...Partners].map((p, i) => (
            <motion.a
              key={i}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              className="flex-shrink-0 w-32 sm:w-40 md:w-48 lg:w-56 mx-4 md:mx-6 p-4 bg-card rounded-xl border border-border/50 shadow-md hover:shadow-xl transition-all duration-300"
            >
              <img
                src={p.image}
                alt="Partner"
                className="w-full h-auto object-contain grayscale hover:grayscale-0 transition-all duration-300"
              />
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
const features = (t: any) => [
  {
    name: t("HomePage.EasyCustomization"),
    icon: <Brush />,
    description: t("HomePage.EasyCustomizationParagraph"),
  },
  {
    name: t(`HomePage.InstantQRCodeAccess`),
    icon: <ScanQrCode />,
    description: t(`HomePage.InstantQRCodeAccessParagraph`),
  },
  {
    name: t("HomePage.ResponsiveDesign"),
    icon: <MonitorSmartphone />,
    description: t("HomePage.ResponsiveDesignParagraph"),
  },
  {
    name: t("HomePage.RealtimeUpdates"),
    icon: <Repeat />,
    description: t("HomePage.RealtimeUpdatesParagraph"),
  },
  {
    name: t("HomePage.MenuAnalytics"),
    icon: <ChartNoAxesColumn />,
    description: t("HomePage.MenuAnalyticsParagraph"),
  },
  {
    name: t("HomePage.CustomerFeedback"),
    icon: <MessageSquare />,
    description: t("HomePage.customerFeedbackParagraph"),
  },
  {
    name: t("HomePage.MultiLanguageSupport"),
    icon: <Languages />,
    description: t("HomePage.multiLanguageSupportParagraph"),
  },
  {
    name: t("HomePage.DynamicLayouts"),
    icon: <LayoutGrid />,
    description: t("HomePage.dynamicLayoutsParagraph"),
  },
  {
    name: t("HomePage.PremiumSupport"),
    icon: <FaHandFist />,
    description: t("HomePage.premiumSupportParagraph"),
  },
];
